import { Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun, Moon, Sun, type LucideIcon } from 'lucide-react';
import { useWeather } from '../../hooks/useWidgetsData';
import { describeWeather } from '../../services/weather';
import { useSettings } from '../../store/settings';

function iconFor(code: number, isDay = true): LucideIcon {
  if (code === 0) return isDay ? Sun : Moon;
  if (code <= 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 57) return CloudDrizzle;
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return CloudRain;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
}

export function Weather({ compact = false }: { compact?: boolean }) {
  const location = useSettings((s) => s.weather.location);
  const { data, error, loading } = useWeather();

  if (!location) {
    return <div className="glass muted p-4 text-sm">Set a location in Settings → Widgets to see the weather.</div>;
  }
  if (!data) {
    return <div className="glass muted p-4 text-sm">{loading ? 'Loading weather…' : (error ?? 'Weather unavailable')}</div>;
  }

  const Now = iconFor(data.current.code, data.current.isDay);
  const days = compact ? data.daily.slice(1, 4) : data.daily.slice(1, 7);
  const fmtDay = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

  return (
    <div className="glass p-4">
      <div className="flex items-center gap-4">
        <Now className="h-12 w-12 shrink-0" strokeWidth={1.5} aria-hidden />
        <div>
          <div className="text-4xl font-light tabular-nums">{Math.round(data.current.temperature)}°</div>
          <div className="muted text-sm">
            {describeWeather(data.current.code)} · {location.name.split(',')[0]}
          </div>
        </div>
        <div className="muted ml-auto text-right text-sm tabular-nums">
          {data.daily[0] && (
            <>
              H {Math.round(data.daily[0].max)}° <br /> L {Math.round(data.daily[0].min)}°
            </>
          )}
        </div>
      </div>
      <div className={`mt-4 grid gap-2 text-center text-sm ${compact ? 'grid-cols-3' : 'grid-cols-6'}`}>
        {days.map((d) => {
          const Icon = iconFor(d.code);
          return (
            <div key={d.date}>
              <div className="muted">{fmtDay.format(new Date(`${d.date}T12:00:00`))}</div>
              <Icon className="mx-auto my-1 h-6 w-6" strokeWidth={1.5} aria-label={describeWeather(d.code)} />
              <div className="tabular-nums">
                {Math.round(d.max)}° <span className="muted">{Math.round(d.min)}°</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
